/**
 * Customers.tsx
 *
 * Date: 08-08-24
 *
 * Description:
 * This component displays a list of customers with functionalities for searching, pagination, and viewing customer details.
 * It includes a breadcrumb for navigation, a search filter input for querying customers, and a table to list customer details.
 * The component fetches customer data from an API and updates the view based on user interactions.
 */

import React, { CSSProperties, FormEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import BreadCrumb from "src/Components/GlobalComponents/BreadCrumb";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import TableWrapper from "src/Components/GlobalComponents/TableWrapper";
import httpRequest from "src/helpers/httpRequest";
import { ICustomerItems, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faXmark } from "@fortawesome/free-solid-svg-icons";
import ToggleRadioBtn from "src/Components/ToggleRadioBtn";
import DeclineModalReason from "src/Components/AdminComponents/DeclineModalReason";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import { CHECKBOX_INPUT } from "src/lib/constans";
import { Modal } from "@mui/material";
import SimpleInput from "src/Components/SimpleInput";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";
import { updateIndicators } from "src/Store/redDots";
import { canWrite } from "src/helpers/helperFunctions";
import moment from "moment";

interface IPayloadState {
  subject: string;
  title: string;
  descripiton: string;
}
const initialState: IPayloadState = {
  subject: "",
  title: "",
  descripiton: "",
};
const initialErroState = {
  subject: "",
  title: "",
  descripiton: "",
};

interface ICustomerResponse {
  documents: ICustomerItems[];
  paginated: IPaginated;
}

const Customers: React.FC = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "userManagement", false);
  // Inline style for the buttons
  const btn__style: CSSProperties = {
    backgroundColor: "#00443f",
    color: "#fff",
    padding: "4px 15px",
    borderRadius: "1.5rem",
    fontWeight: "400",
  };

  // decline with reason
  const [declineId, setDeclineId] = useState<string>("");
  const [declineLoader, setDeclineLoader] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  // Closes the decline modal
  const closeModal = () => {
    setDeclineId("");
    setMessage("");
  };

  const activeCall = async (id: string) => {
    setDeclineLoader(id);
    setAllCustomers((prev) =>
      prev.map((v) => (v._id === id ? { ...v, accountStatusByAdmin: true } : v))
    );
    // Build the params object conditionally
    const { res, err } = await httpRequest<{ message: string }>({
      path: `/customer/account/status/${id}`,
    });
    if (res) {
      toast(res.message, { type: "success" });
    } else {
      toast(err, { type: "error" });
      setAllCustomers((prev) =>
        prev.map((v) =>
          v._id === id ? { ...v, accountStatusByAdmin: false } : v
        )
      );
    }
    setDeclineLoader(null);
  };

  /**
   * API_CALL: POST /customer/account/status/${id}
   */
  const handleStatusToogle = async (id: string) => {
    if (!hasWriteAccess) {
      return;
    }

    if (!message) {
      toast("Please enter reason!", { type: "error" });
      return;
    }
    setDeclineLoader(id);
    // Build the params object conditionally
    const { res, err } = await httpRequest<{ message: string; data: boolean }>({
      method: "post",
      path: "/customer/account/status",
      params: { customerId: id, message },
    });
    if (res) {
      toast(res.message, { type: "success" });
      setAllCustomers((prev) =>
        prev.map((v) =>
          v._id === id ? { ...v, accountStatusByAdmin: res.data } : v
        )
      );
      closeModal();
    } else {
      toast(err, { type: "error" });
    }
    setDeclineLoader(null);
  };
  // decline with reason

  // States for loading status, pagination, and customer data
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [allCustomers, setAllCustomers] = useState<ICustomerItems[]>([]);

  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    search: "",
  });

  // Updates search text in query parameters
  const handleChangeText = (txt: string) => {
    setQueryParams((prev) => ({
      ...prev,
      search: txt,
      page: 1,
    }));
  };

  // Handles page change in pagination
  const handlePageChange = (e: number) => {
    setSelectedRows([]);
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const handleSelectAll = () => {
    setSelectedRows(allCustomers.map((v) => v._id));
  };
  const onToogleSelect = (id: string) => {
    if (selectedRows.includes(id)) {
      // if is exist
      setSelectedRows((prev) => prev.filter((v) => v !== id));
    } else {
      setSelectedRows((prev) => [...prev, id]);
    }
  };

  const [emailModal, setEmailModal] = useState<boolean>(false);
  const [postLoader, setPostLoader] = useState<boolean>(false);
  const [errorField, setErrorField] = useState(initialErroState);
  const [payload, setPayload] = useState<IPayloadState>(initialState);
  const handleChange = (key: keyof IPayloadState, val: string) => {
    setPayload((prev) => ({ ...prev, [key]: val }));
    if (val) {
      setErrorField((prev) => ({ ...prev, [key]: "" }));
    }
  };
  const onClose = () => {
    setEmailModal(false);
    setSelectedRows([]);
    setErrorField(initialErroState);
    setPayload(initialState);
  };

  const validateFields = () => {
    const errors: typeof errorField = {
      subject: payload?.subject ? "" : "Subject is required.",
      title: payload.title ? "" : "Title is required.",
      descripiton: payload.descripiton ? "" : "Description is required.",
    };
    setErrorField(errors);
    // Check if there are any errors
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (validateFields()) {
      setPostLoader(true);
      const { res, err } = await httpRequest<{ data: string }>({
        method: "post",
        path: "/customer/sendemail",
        params: { ...payload, customers: selectedRows },
      });
      if (res) {
        toast(res?.data || "Success!", { type: "success" });
        onClose();
      } else {
        toast(err, { type: "error" });
      }
      setPostLoader(false);
    }
  };

  // API_CALL: Fetches all customers from the server
  const fetchAllCustomers = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<ICustomerResponse>({
      path: `/customer?${qs.stringify(queryParams)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      setAllCustomers(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    fetchAllCustomers();
    dispatch(updateIndicators({ customers_indicators: false }));
  }, [queryParams]);

  const BackButton: React.FC = () => {
    return (
      <div className="mt-2 flex justify-center">
        <PrimaryBtn
          onPress={() => handleStatusToogle(declineId)}
          type="button"
          loader={declineLoader ? true : false}
          ButtonText="Send"
          btnExtraStyle={{
            borderRadius: "100rem",
            backgroundColor: "#004540",
          }}
        />
      </div>
    );
  }; // display back icon in page header

  return (
    <>
      <BreadCrumb
        current="Customers"
        previous="dashboard"
        navigateUrl="/admin"
        btnShow={false}
      />
      <Modal
        open={emailModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="flex items-center flex-col gap-2 bg-[#FFFCF1] w-[600px] p-[20px] rounded-[8px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <button
            onClick={onClose}
            className="absolute top-[-10px] right-[-10px] bg-red-500 rounded-full w-5 h-5 flex justify-center items-center"
          >
            <FontAwesomeIcon
              className="font-size-16px text-white"
              icon={faXmark}
              size="2x"
            />
          </button>
          {/* <img src={modalIcon} alt="icon" width={60} /> */}
          <h1 className="font-size-24px theme-color-green font-Poppins-SemiBold text-center w-[400px] leading-1">
            Compose Email
          </h1>
          <form onSubmit={handleSubmit} className="w-full compose-email-modal">
            <div>
              <SimpleInput
                value={payload.subject}
                onChange={(val) => handleChange("subject", val)}
                type="text"
                placeholder="Subject"
                rows={7}
                extraStyle={{ borderColor: "#009d91" }}
              />
              {errorField?.subject && (
                <p className="font-size-14px text-[#ff0000] font-Poppins-Regular mb-2">
                  {errorField?.subject}
                </p>
              )}
            </div>
            <div>
              <SimpleInput
                value={payload.title}
                onChange={(val) => handleChange("title", val)}
                type="text"
                placeholder="Title"
                rows={7}
                extraStyle={{ borderColor: "#009d91" }}
              />
              {errorField?.title && (
                <p className="font-size-14px text-[#ff0000] font-Poppins-Regular mb-2">
                  {errorField?.title}
                </p>
              )}
            </div>
            <div>
              <SimpleInput
                value={payload.descripiton}
                onChange={(val) => handleChange("descripiton", val)}
                type="textArea"
                placeholder="Description"
                rows={7}
                extraStyle={{ borderColor: "#009d91" }}
              />
              {errorField?.descripiton && (
                <p className="font-size-14px text-[#ff0000] font-Poppins-Regular mb-2">
                  {errorField?.descripiton}
                </p>
              )}
            </div>
            <div className="flex justify-end">
              <PrimaryBtn
                loader={postLoader}
                type="submit"
                ButtonText="Send"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                  padding: "0.5rem 2rem",
                }}
              />
            </div>
          </form>
        </div>
      </Modal>

      <div className="all-customers">
        <DeclineModalReason
          title={`Please specify the reason of deactivation`}
          value={message}
          setValue={setMessage}
          isOpen={declineId ? true : false}
          onClose={closeModal}
          ButtonComponent={BackButton}
        />
        <div className="table-container">
          <SearchFilterInput onChangeText={handleChangeText} />
          {/* <div className="flex justify-end mb-3">
                        <PrimaryBtn
                            btnExtralClasses={selectedRows.length ? "" : "cursor-not-allowed"}
                            disabled={!selectedRows.length}
                            title={selectedRows.length ? "" : "Please select customer."}
                            ButtonText="Email"
                            btnExtraStyle={{ backgroundColor: "#00443f", borderRadius: "1.5rem", padding: "0.5rem 2rem" }}
                            onPress={() => {
                                if (!hasWriteAccess) {
                                    return
                                }
                                else {
                                    setEmailModal(true)
                                }
                            }}
                        />
                    </div> */}
          <TableWrapper
            isLoading={isLoader}
            page={paginated?.currentPage || 1}
            total={paginated?.totalPages || 0}
            thead={[
              "S.No",
              "Customer Name",
              "Email",
              "Date of Birth",
              "Status",
              "Health Profile",
              "Action",
            ]}
            onChange={handlePageChange}
            columnWidth={"6% 15% 20% 15% 12% 10% 22%"}
            selectedRows={selectedRows}
            totalLength={allCustomers?.length || 1}
            clearAll={() => setSelectedRows([])}
            selectAll={handleSelectAll}
          >
            <div className="tbody-content-height">
              {allCustomers?.map((item: ICustomerItems, index: number) => (
                <ul
                  key={index}
                  className={`font-size-16px tbody-light-table font-Poppins-Regular grid ${
                    !item?.adminNotified ? "font-bold" : ""
                  }`}
                  style={{ gridTemplateColumns: "6% 15% 20% 15% 12% 10% 22%" }}
                >
                  <li>
                    {(
                      (paginated?.currentPage - 1) * queryParams.limit +
                      (index + 1)
                    )
                      .toString()
                      .padStart(2, "0")}
                  </li>
                  {/* <li className={(item?.unRead || item?.unReadNotified) ? "font-bold" : ""}> */}
                  <li>
                    <Link
                      id={`customer-name-btn-${index + 1}`}
                      className={`${
                        ((item?.firstName || item?.lastName) && !item?.adminNotified) ? "underline" : "hover:underline"
                      } capitalize ${
                        !hasWriteAccess ? "cursor-not-allowed" : ""
                      }`}
                      to={!hasWriteAccess ? "#" : `/customer/${item?._id}/View`}
                    >
                      {item?.firstName || item?.lastName
                        ? `${item?.firstName || ""} ${item?.lastName || ""}`
                        : "-"}
                    </Link>
                    {/* {item?.unReadNotified ?
                                            <div className="red-dot-show-only-dot ml-2"><p></p></div>
                                            : null
                                        } */}
                  </li>
                  <li>
                    <span className="truncate block">{item?.email || ""}</span>
                  </li>
                  <li>
                    {item?.dob ? moment(item?.dob).format("DD-MM-YYYY") : "-"}
                  </li>
                  <li
                    className="justify-center"
                    style={{ justifyContent: "center", paddingLeft: 0 }}
                  >
                    {item?.isDeleted ? (
                      <div className="font-size-14px font-Poppins-Regular bg-[#ff00003b] text-[red] rounded-[1.5rem] py-1 px-3">
                        Deleted
                      </div>
                    ) : (
                      <div
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                      >
                        <ToggleRadioBtn
                          isToggled={item.accountStatusByAdmin}
                          handleToggle={() => {
                            if (!hasWriteAccess) {
                              return;
                            } else {
                              item.accountStatusByAdmin
                                ? setDeclineId(item._id)
                                : activeCall(item._id);
                            }
                          }}
                          loader={item._id === declineLoader ? true : false}
                          disabled={!hasWriteAccess}
                          id={`toggle-radio-btn-${index + 1}`}
                        />
                      </div>
                    )}
                  </li>
                  <li
                    className="action-last-colum justify-center"
                    style={{ justifyContent: "center" }}
                  >
                    <Link
                      id={`health-profile-btn-${index + 1}`}
                      className=""
                      to={
                        !hasWriteAccess
                          ? "#"
                          : `/health-profile/${item?._id}/View`
                      }
                    >
                      <FontAwesomeIcon
                        icon={faEye}
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                      />
                    </Link>
                  </li>
                  <li>
                    <div className="flex gap-2">
                      <Link
                        id={`all-tests-btn-${index + 1}`}
                        style={btn__style}
                        to={
                          !hasWriteAccess ? "#" : `/view/${item?._id}/all-test`
                        }
                        className={`normal_fontweight ${
                          !hasWriteAccess ? "cursor-not-allowed" : ""
                        }`}
                      >
                        All Tests
                      </Link>
                      <Link
                        id={`add-test-btn-${index + 1}`}
                        className={`relative normal_fontweight ${
                          !hasWriteAccess ? "cursor-not-allowed" : ""
                        }`}
                        style={btn__style}
                        to={
                          !hasWriteAccess
                            ? "#"
                            : `/add-result/${item?._id}/View`
                        }
                      >
                        Add Test
                        {item?.diagnosticTestNotified === false && (
                          <div className="red-dot-show absolute red-dot-show-position-set"></div>
                        )}
                      </Link>
                    </div>
                  </li>
                </ul>
              ))}
            </div>
          </TableWrapper>
        </div>
      </div>
    </>
  );
};

export default Customers;
